// LocationMap wraps Leaflet for the one job we need: let someone drop a pin.
//
// Kept separate from <geo-locator> so both the customer's request form and the
// manufacturer's profile drive the same map with the same behaviour.
//
// Leaflet's default marker is a PNG resolved from a base URL, which breaks under
// a bundler unless the images are copied and the path patched. We draw the pin
// with a divIcon instead: pure CSS, no image assets, nothing to 404.
import L from "leaflet";

const LIMA: L.LatLngTuple = [-12.0464, -77.0428];

export interface MapMoveDetail {
  lat: number;
  lng: number;
}

export class LocationMap {
  private map: L.Map;
  private marker: L.Marker;
  private onMove: (d: MapMoveDetail) => void;

  constructor(container: HTMLElement, onMove: (d: MapMoveDetail) => void) {
    this.onMove = onMove;

    this.map = L.map(container, {
      center: LIMA,
      zoom: 11,
      // A map inside a long form is a scroll trap: the page stops scrolling the
      // moment the wheel lands on it. Zoom stays on the buttons and the keyboard.
      scrollWheelZoom: false,
    });

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
      attribution: "&copy; OpenStreetMap",
    }).addTo(this.map);

    this.marker = L.marker(LIMA, {
      draggable: true,
      icon: L.divIcon({
        className: "map-pin",
        html: "<span class=\"map-pin__dot\"></span>",
        iconSize: [24, 24],
        iconAnchor: [12, 12],
      }),
      keyboard: true,
      title: "Arrástrame para precisar la ubicación",
    }).addTo(this.map);

    this.marker.on("dragend", (): void => this.emit());
    // Clicking the map is faster than dragging on a phone.
    this.map.on("click", (e: L.LeafletMouseEvent): void => {
      this.marker.setLatLng(e.latlng);
      this.emit();
    });

    // Leaflet measures its container once, on init. Inside a form that is still
    // laying out (grid, fonts, a collapsing sidebar) that measurement is wrong
    // and the map renders as a torn postage stamp. Re-measure whenever the box
    // actually changes size, instead of guessing when layout has settled.
    new ResizeObserver((): void => {
      this.map.invalidateSize();
    }).observe(container);
  }

  private emit(): void {
    const { lat, lng } = this.marker.getLatLng();
    this.onMove({ lat, lng });
  }

  /**
   * Moves the pin without firing onMove — used when the district drives the map.
   *
   * `animate` flies the map there instead of teleporting. It matters more than it
   * sounds: the map always re-centres on the pin, so the pin never moves on screen —
   * the map slides underneath it. Jump instantly between two districts of the same
   * city and the pin sits on the same pixel over a near-identical background, which
   * reads as "nothing happened". The flight is the only thing that shows the choice
   * landed.
   *
   * Guards the coordinates: a client holding a stale cached district payload (the
   * endpoint is cached for a day) can hand us undefined, and Leaflet throws
   * "Invalid LatLng" and takes the rest of the component down with it.
   */
  setPin(lat: number, lng: number, zoom?: number, opts: { animate?: boolean } = {}): void {
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
    this.marker.setLatLng([lat, lng]);
    const target = zoom ?? this.map.getZoom();
    if (opts.animate) {
      this.map.flyTo([lat, lng], target, { duration: 0.8 });
    } else {
      this.map.setView([lat, lng], target);
    }
    this.pulse();
  }

  /** A one-shot pulse on the pin, so the eye lands on where it ended up. */
  private pulse(): void {
    const el = this.marker.getElement();
    if (!el) return;
    el.classList.remove("map-pin--pulse");
    void el.offsetWidth; // restart the animation even if it is already running
    el.classList.add("map-pin--pulse");
  }

  /** Leaflet miscalculates its size when it starts hidden; call once revealed. */
  refresh(): void {
    this.map.invalidateSize();
  }
}
